import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import GradeInput from "../../GradeInput";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { normalizeDate } from "../../../common/normalizeDate";
import { ProtocolGradeResponse } from "../../../services/protocolsService";
import { formatDateOnlyValue } from "../../../common/dateOnly";

interface RecognitionProtocolTableProps {
  grades: ProtocolGradeResponse[];
  isSigned: boolean;
  modifiedRows: Set<number>;
  gradeTypeOptions: { id: number; name: string }[];
  onGradeChange: (gradeId: number, newGrade: number) => void;
  onGradeTypeChange: (gradeId: number, newGradeTypeId: number) => void;
  onDateChange: (gradeId: number, date: Date | null) => void;
}

const RecognitionProtocolTable: React.FC<RecognitionProtocolTableProps> = ({
  grades,
  isSigned,
  modifiedRows,
  gradeTypeOptions,
  onGradeChange,
  onGradeTypeChange,
  onDateChange,
}) => {
  const { t } = useTranslation();

  const [sortConfig, setSortConfig] = useState<{
    key: "subjectName" | "semester" | null;
    direction: "asc" | "desc";
  }>(() => {
    const savedSort = localStorage.getItem("recognition-protocol-sort-preference");
    return savedSort ? JSON.parse(savedSort) : { key: null, direction: "asc" };
  });

  const sortedGrades = useMemo(() => {
    if (!sortConfig.key) return grades;

    return [...grades].sort((a, b) => {
      if (sortConfig.key === "semester") {
        const aVal = a.semester || 0;
        const bVal = b.semester || 0;
        return sortConfig.direction === "asc" ? aVal - bVal : bVal - aVal;
      }

      const aVal = a[sortConfig.key as keyof typeof a]?.toString().toLowerCase() ?? "";
      const bVal = b[sortConfig.key as keyof typeof b]?.toString().toLowerCase() ?? "";

      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [grades, sortConfig]);

  const handleSort = (key: "subjectName" | "semester") => {
    setSortConfig((current) => {
      const newConfig: {
        key: "subjectName" | "semester" | null;
        direction: "asc" | "desc";
      } = {
        key,
        direction: current.key === key && current.direction === "asc" ? "desc" : "asc",
      };
      localStorage.setItem("recognition-protocol-sort-preference", JSON.stringify(newConfig));
      return newConfig;
    });
  };

  const SortIndicator: React.FC<{ column: string }> = ({ column }) => {
    if (sortConfig.key !== column) return <span className="ml-1">↕</span>;
    return (
      <span className="ml-1">{sortConfig.direction === "asc" ? "↑" : "↓"}</span>
    );
  };

  return (
    <div className="overflow-x-auto bg-white">
      <table className="min-w-full border border-gray-300 dark:bg-gray-800">
        <thead>
          <tr className="bg-bodydark text-white text-left dark:bg-gray-100 dark:text-black">
            <th
              className="py-2 px-4 border-b dark:border-gray-600 cursor-pointer hover:bg-opacity-80"
              onClick={() => handleSort("subjectName")}
            >
              {t("Subject")}
              <SortIndicator column="subjectName" />
            </th>
            <th
              className="py-2 px-4 border-b dark:border-gray-600 cursor-pointer hover:bg-opacity-80"
              onClick={() => handleSort("semester")}
            >
              {t("Semester")}
              <SortIndicator column="semester" />
            </th>
            <th className="py-2 px-4 border-b dark:border-gray-600">
              {t("Grade")}
            </th>
            <th className="py-2 px-4 border-b dark:border-gray-600">
              {t("Grade Type")}
            </th>
            <th className="py-2 px-4 border-b dark:border-gray-600">
              {t("Date")}
            </th>
            <th className="py-2 px-2 border-b dark:border-gray-600 w-8"></th>
          </tr>
        </thead>
        <tbody>
          {sortedGrades.length > 0 ? (
            sortedGrades.map((grade) => (
              <tr
                key={grade.studentGradeId}
                className="border-b dark:border-gray-600"
              >
                <td className="py-2 px-4 dark:text-white">
                  {grade.subjectName || t("N/A")}
                </td>
                <td className="py-2 px-4 dark:text-white">
                  {grade.semester || t("N/A")}
                </td>
                <td className="py-2 px-4 dark:text-white">
                  <div className="flex items-center">
                    {!isSigned ? (
                      <GradeInput
                        value={grade.grade}
                        onChange={(value) => onGradeChange(grade.studentGradeId, value)}
                        className="w-24"
                        disabled={isSigned}
                      />
                    ) : (
                      <span className="mx-2">
                        {grade.gradeName} {grade.grade}
                      </span>
                    )}
                  </div>
                </td>
                <td className="py-2 px-4 dark:text-white">
                  <div className="flex items-center">
                    {isSigned ? (
                      <span className="mx-2">
                        {grade.studentGradeTypeName}
                      </span>
                    ) : (
                      <select
                        value={grade.studentGradeTypeId}
                        onChange={(e) =>
                          onGradeTypeChange(grade.studentGradeId, parseInt(e.target.value))
                        }
                        className="bg-gray-200 border border-gray-400 rounded-md p-2 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        disabled={isSigned}
                      >
                        <option value="" disabled>
                          {t("Select type")}
                        </option>
                        {gradeTypeOptions.map((option) => (
                          <option
                            key={option.id}
                            value={option.id}
                            className="dark:bg-gray-800"
                          >
                            {t(option.name)}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                </td>
                <td className="py-2 px-4 dark:text-white">
                  {isSigned ? (
                    <span>
                      {grade.updateDate
                        ? formatDateOnlyValue(grade.updateDate)
                        : "-"}
                    </span>
                  ) : (
                    <DatePicker
                      selected={
                        grade.updateDate ? normalizeDate(grade.updateDate) : null
                      }
                      onChange={(date: Date | null) =>
                        onDateChange(grade.studentGradeId, date)
                      }
                      dateFormat="dd.MM.yyyy"
                      className="bg-gray-200 border border-gray-400 rounded-md p-2 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      placeholderText={t("Select date")}
                      showTimeSelect={false}
                      disabled={isSigned}
                    />
                  )}
                  <div className="text-xs text-gray-400">
                    {grade.modifiedByUsername}
                  </div>
                </td>
                <td className="py-2 px-2 text-center">
                  {modifiedRows.has(grade.studentGradeId) && (
                    <div className="w-4 h-4 bg-green-500 rounded-full mx-auto"></div>
                  )}
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={6} className="py-2 px-4 text-center dark:text-white">
                {t("No grades available.")}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default RecognitionProtocolTable; 
